import { useEffect, useState } from 'react'
import Combobox from './Combobox.jsx'

/**
 * Cascading + searchable Thai address selector.
 *   จังหวัด → อำเภอ/เขต → ตำบล/แขวง  (each a searchable combobox)
 *   รหัสไปรษณีย์ auto-fills from the chosen subdistrict (read-only).
 *
 * Data: /thai-locations.json (nested { province: { district: { subdistrict: zip } } }),
 * generated from the official Thailand location list. Fetched once and cached.
 *
 * value = { line1, subdistrict, district, province, postcode }
 */

let CACHE = null
let PROMISE = null
function loadLocations() {
  if (CACHE) return Promise.resolve(CACHE)
  if (PROMISE) return PROMISE
  PROMISE = fetch('/thai-locations.json')
    .then((r) => {
      const ct = r.headers.get('content-type') || ''
      // A dev server with a stale build serves the SPA index.html (HTML, 200)
      // for unknown paths — guard so we never JSON.parse "<!doctype …".
      if (!r.ok || !ct.includes('json')) {
        console.warn('[AddressSelect] /thai-locations.json not served as JSON — rebuild dist / restart the local server.')
        return {}
      }
      return r.json()
    })
    .then((d) => { CACHE = d; return d })
    .catch(() => ({}))
  return PROMISE
}

const thSort = (a, b) => a.localeCompare(b, 'th')
function provinceList(data) {
  const keys = Object.keys(data).sort(thSort)
  const bkk = 'กรุงเทพมหานคร'
  return keys.includes(bkk) ? [bkk, ...keys.filter((k) => k !== bkk)] : keys
}

const LABEL = 'block text-[13.5px] font-medium text-slate-500 mb-1.5'
const INPUT =
  'w-full rounded-xl border border-slate-200/90 bg-slate-50/60 px-3.5 py-2.5 text-[15px] text-slate-800 ' +
  'placeholder:text-slate-400 outline-none transition-all duration-150 ' +
  'hover:border-slate-300 focus:bg-white focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

function Field({ label, children }) {
  return <div><span className={LABEL}>{label}</span>{children}</div>
}

export default function AddressSelect({ value, onChange, disabled }) {
  const [data, setData] = useState(CACHE)

  useEffect(() => {
    let alive = true
    loadLocations().then((d) => { if (alive) setData(d) })
    return () => { alive = false }
  }, [])

  const v = value || {}
  const province = v.province || ''
  const district = v.district || ''
  const subdistrict = v.subdistrict || ''

  const provinces = data ? provinceList(data) : []
  const districts = (data && province && data[province]) ? Object.keys(data[province]).sort(thSort) : []
  const subdistricts = (data && province && district && data[province]?.[district]) ? Object.keys(data[province][district]).sort(thSort) : []

  const setProvince = (p) => onChange({ ...v, province: p, district: '', subdistrict: '', postcode: '' })
  const setDistrict = (d) => onChange({ ...v, district: d, subdistrict: '', postcode: '' })
  const setSubdistrict = (s) => {
    const zip = data?.[province]?.[district]?.[s] || ''
    onChange({ ...v, subdistrict: s, postcode: zip })
  }

  return (
    <div className="space-y-4">
      <Field label="ที่อยู่ (บ้านเลขที่ / หมู่ / ถนน)">
        <input
          className={INPUT}
          disabled={disabled}
          value={v.line1 || ''}
          onChange={(e) => onChange({ ...v, line1: e.target.value })}
          placeholder="เช่น 99/1 หมู่ 2 ถนนสุขุมวิท"
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4">
        <Field label="จังหวัด">
          <Combobox value={province} onChange={setProvince} options={provinces} placeholder="เลือก / ค้นหาจังหวัด" disabled={disabled} />
        </Field>
        <Field label="อำเภอ / เขต">
          <Combobox value={district} onChange={setDistrict} options={districts} placeholder={province ? 'เลือก / ค้นหาอำเภอ' : 'เลือกจังหวัดก่อน'} disabled={disabled || !province} />
        </Field>
        <Field label="ตำบล / แขวง">
          <Combobox value={subdistrict} onChange={setSubdistrict} options={subdistricts} placeholder={district ? 'เลือก / ค้นหาตำบล' : 'เลือกอำเภอก่อน'} disabled={disabled || !district} />
        </Field>
        <Field label="รหัสไปรษณีย์">
          <div className="w-full rounded-xl border border-slate-200 bg-slate-100/70 px-3.5 py-2.5 text-[15px] text-slate-500 flex items-center justify-between gap-2 select-none">
            <span className="tabular-nums">{v.postcode || '—'}</span>
            <span className="text-[12px] uppercase tracking-wide text-slate-400">อัตโนมัติ</span>
          </div>
        </Field>
      </div>
    </div>
  )
}
